import { type FormEvent, useEffect, useMemo, useState } from "react";
import {
  createAdminUser,
  deleteAdminUser,
  fetchAdminUser,
  fetchAdminUsers,
  type AdminUserItem,
  updateAdminUser
} from "../../lib/admin-users";

type EditorState = {
  id: number | null;
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  phone: string;
  role: "ADMIN" | "SALES" | "Customer";
  password: string;
  emailConfirmed: boolean;
  address: string;
  profilePicture: string;
};

const emptyEditorState: EditorState = {
  id: null,
  firstName: "",
  middleName: "",
  lastName: "",
  email: "",
  phone: "",
  role: "Customer",
  password: "",
  emailConfirmed: false,
  address: "",
  profilePicture: ""
};

function toEditorState(user: AdminUserItem): EditorState {
  return {
    id: user.id,
    firstName: user.firstName,
    middleName: user.middleName,
    lastName: user.lastName,
    email: user.email,
    phone: user.phone,
    role: user.role,
    password: "",
    emailConfirmed: user.emailConfirmed,
    address: user.address,
    profilePicture: user.profilePicture
  };
}

function initialsForUser(user: Pick<AdminUserItem, "firstName" | "lastName" | "email">) {
  const source = `${user.firstName} ${user.lastName}`.trim() || user.email || "U";
  return source
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
}

function roleTone(role: AdminUserItem["role"]) {
  if (role === "ADMIN") {
    return "border-red-200 bg-red-50 text-red-700 dark:border-red-900/70 dark:bg-red-950/40 dark:text-red-300";
  }
  if (role === "SALES") {
    return "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/70 dark:bg-blue-950/40 dark:text-blue-300";
  }
  return "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/40 dark:text-emerald-300";
}

export function AdminUsersPage() {
  const [users, setUsers] = useState<AdminUserItem[]>([]);
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState<"all" | "ADMIN" | "SALES" | "Customer">("all");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 25,
    totalUsers: 0,
    totalPages: 1,
    hasPreviousPage: false,
    hasNextPage: false
  });
  const [summary, setSummary] = useState({
    totalUsers: 0,
    admins: 0,
    sales: 0,
    customers: 0
  });
  const [loading, setLoading] = useState(true);
  const [loadingEditor, setLoadingEditor] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editor, setEditor] = useState<EditorState>(emptyEditorState);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [userToDelete, setUserToDelete] = useState<AdminUserItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  const metrics = useMemo(
    () => [
      ["Total users", summary.totalUsers],
      ["Admins", summary.admins],
      ["Sales", summary.sales],
      ["Customers", summary.customers]
    ],
    [summary]
  );

  useEffect(() => {
    let ignore = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();
        if (query.trim()) {
          params.set("q", query.trim());
        }
        params.set("role", roleFilter);
        params.set("page", String(page));
        params.set("pageSize", "25");

        const result = await fetchAdminUsers(params);
        if (!ignore) {
          setUsers(result.users);
          setPagination(result.pagination);
          setSummary(result.summary);
        }
      } catch (loadError) {
        if (!ignore) {
          setError(loadError instanceof Error ? loadError.message : "Unable to load users.");
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    void load();

    return () => {
      ignore = true;
    };
  }, [page, query, roleFilter]);

  useEffect(() => {
    setPage(1);
  }, [query, roleFilter]);

  async function reloadCurrentView() {
    const params = new URLSearchParams();
    if (query.trim()) {
      params.set("q", query.trim());
    }
    params.set("role", roleFilter);
    params.set("page", String(page));
    params.set("pageSize", "25");
    const result = await fetchAdminUsers(params);
    setUsers(result.users);
    setPagination(result.pagination);
    setSummary(result.summary);
  }

  function openCreate() {
    setEditor(emptyEditorState);
    setEditorOpen(true);
    setLoadingEditor(false);
    setError(null);
    setSuccessMessage(null);
  }

  async function openEdit(user: AdminUserItem) {
    setEditorOpen(true);
    setLoadingEditor(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const detail = await fetchAdminUser(user.id);
      setEditor(toEditorState(detail));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Unable to load user details.");
    } finally {
      setLoadingEditor(false);
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();

    if (!editor.firstName.trim()) {
      setError("First name is required.");
      return;
    }

    if (!editor.id && editor.password.trim().length < 8) {
      setError("New users must have a password of at least 8 characters.");
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const payload = {
        firstName: editor.firstName,
        middleName: editor.middleName,
        lastName: editor.lastName,
        email: editor.email,
        phone: editor.phone,
        role: editor.role,
        password: editor.password,
        emailConfirmed: editor.emailConfirmed,
        address: editor.address,
        profilePicture: editor.profilePicture
      };

      let savedUserId = editor.id;

      if (editor.id) {
        await updateAdminUser(editor.id, payload);
      } else {
        const created = await createAdminUser(payload);
        savedUserId = typeof created?.userId === "number" ? created.userId : null;
      }

      await reloadCurrentView();
      if (savedUserId) {
        const refreshed = await fetchAdminUser(savedUserId);
        setEditor(toEditorState(refreshed));
      }
      setSuccessMessage(editor.id ? "User changes saved successfully." : "User created successfully.");
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Unable to save user.");
    } finally {
      setSaving(false);
    }
  }

  function handleDelete(user: AdminUserItem) {
    setUserToDelete(user);
  }

  async function handleDeleteConfirm() {
    if (!userToDelete) return;
    setDeleting(true);
    try {
      setError(null);
      setSuccessMessage(null);
      await deleteAdminUser(userToDelete.id);
      await reloadCurrentView();
      if (editor.id === userToDelete.id) {
        setEditor(emptyEditorState);
        setEditorOpen(false);
      }
      setSuccessMessage(`User "${[userToDelete.firstName, userToDelete.lastName].filter(Boolean).join(" ") || userToDelete.email}" was successfully deleted.`);
      setUserToDelete(null);
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : "Unable to delete user.");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">Users</p>
          <h1 className="mt-3 text-4xl font-semibold tracking-tight">Accounts and permissions</h1>
          <p className="mt-4 max-w-3xl text-nv-slate">
            Manage staff and customer accounts, control role access, and keep operational identity data clean and auditable.
          </p>
        </div>
        <button className="brand-button rounded-full px-5 py-3 text-sm font-semibold text-white" onClick={openCreate} type="button">
          New user
        </button>
      </div>

      <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[minmax(0,1.45fr)_minmax(320px,1fr)]">
        <div className="min-h-0 space-y-4 overflow-y-auto pr-1">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {metrics.map(([label, value]) => (
              <div className="admin-card rounded-[28px] p-5" key={label}>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">{label}</p>
                <p className="mt-3 text-3xl font-semibold tracking-tight text-nv-ink">{value}</p>
              </div>
            ))}
          </div>

          <div className="admin-card rounded-[28px] p-5">
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_180px]">
              <input
                className="field-control px-4 py-3 text-sm"
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search by name, email, or phone"
                value={query}
              />
              <select className="field-control px-4 py-3 text-sm" onChange={(event) => setRoleFilter(event.target.value as typeof roleFilter)} value={roleFilter}>
                <option value="all">All roles</option>
                <option value="ADMIN">Admins</option>
                <option value="SALES">Sales</option>
                <option value="Customer">Customers</option>
              </select>
            </div>
          </div>

          {error ? <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/70 dark:bg-red-950/40 dark:text-red-300">{error}</div> : null}

          <div className="admin-card overflow-hidden rounded-[32px]">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1040px]">
                <thead>
                  <tr className="border-b border-black/10 text-left text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">
                    <th className="px-5 py-4">User</th>
                    <th className="px-5 py-4">Role</th>
                    <th className="px-5 py-4">Contact</th>
                    <th className="px-5 py-4">Email status</th>
                    <th className="px-5 py-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td className="px-5 py-8 text-sm text-nv-slate" colSpan={5}>
                        Loading users...
                      </td>
                    </tr>
                  ) : users.length > 0 ? (
                    users.map((user) => (
                      <tr
                        className="cursor-pointer border-b border-black/5 align-top transition-colors hover:bg-black/[0.03] dark:hover:bg-white/[0.03]"
                        key={user.id}
                        onClick={() => void openEdit(user)}
                      >
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-4">
                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-black/10 bg-black/[0.04] text-sm font-semibold text-nv-ink dark:border-white/10 dark:bg-white/[0.04]">
                              {initialsForUser(user)}
                            </div>
                            <div className="min-w-0">
                              <p className="truncate font-semibold text-nv-ink">
                                {[user.firstName, user.middleName, user.lastName].filter(Boolean).join(" ") || "Unnamed user"}
                              </p>
                              <p className="mt-1 truncate text-xs text-nv-slate">#{user.id}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${roleTone(user.role)}`}>{user.role}</span>
                        </td>
                        <td className="px-5 py-4 text-sm text-nv-slate">
                          <p>{user.email || "No email"}</p>
                          <p className="mt-1">{user.phone || "No phone"}</p>
                        </td>
                        <td className="px-5 py-4 text-sm text-nv-slate">{user.emailConfirmed ? "Confirmed" : "Unconfirmed"}</td>
                        <td className="px-5 py-4">
                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              className="rounded-full border border-black/10 px-4 py-2 text-sm font-semibold dark:border-white/10"
                              onClick={(event) => {
                                event.stopPropagation();
                                void openEdit(user);
                              }}
                              type="button"
                            >
                              Edit
                            </button>
                            <button
                              className="rounded-full border border-red-200 px-4 py-2 text-sm font-semibold text-red-700 dark:border-red-900/70 dark:text-red-300"
                              onClick={(event) => {
                                event.stopPropagation();
                                void handleDelete(user);
                              }}
                              type="button"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="px-5 py-8 text-sm text-nv-slate" colSpan={5}>
                        No users matched the current view.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-black/5 px-5 py-4 text-sm text-nv-slate dark:border-white/10">
              <p>
                Page {pagination.page} of {pagination.totalPages} · {pagination.totalUsers} users
              </p>
              <div className="flex items-center gap-2">
                <button
                  className="rounded-full border border-black/10 px-4 py-2 font-semibold disabled:opacity-40 dark:border-white/10"
                  disabled={!pagination.hasPreviousPage || loading}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                  type="button"
                >
                  Previous
                </button>
                <button
                  className="rounded-full border border-black/10 px-4 py-2 font-semibold disabled:opacity-40 dark:border-white/10"
                  disabled={!pagination.hasNextPage || loading}
                  onClick={() => setPage((current) => current + 1)}
                  type="button"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="min-h-0 overflow-y-auto pr-1">
          <div className="admin-card rounded-[32px] p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">{editor.id ? "Edit user" : "Create user"}</p>
                <h2 className="mt-2 text-2xl font-semibold tracking-tight text-nv-ink">
                  {editor.id ? [editor.firstName, editor.lastName].filter(Boolean).join(" ") || "User account" : "New user account"}
                </h2>
              </div>
              {editorOpen ? (
                <button className="rounded-full border border-black/10 px-4 py-2 text-sm font-semibold dark:border-white/10" onClick={() => setEditorOpen(false)} type="button">
                  Close
                </button>
              ) : null}
            </div>

            {!editorOpen ? (
              <div className="mt-6 rounded-3xl border border-dashed border-black/10 p-5 text-sm text-nv-slate dark:border-white/10">
                Select a user to edit, or create a new account.
              </div>
            ) : loadingEditor ? (
              <div className="mt-6 rounded-3xl border border-dashed border-black/10 p-5 text-sm text-nv-slate dark:border-white/10">
                Loading user details...
              </div>
            ) : (
              <form className="mt-6 space-y-5" onSubmit={(event) => void handleSubmit(event)}>
                {successMessage ? (
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/40 dark:text-emerald-300">
                    {successMessage}
                  </div>
                ) : null}

                <div className="grid gap-5 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">First name</label>
                    <input className="field-control px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, firstName: event.target.value }))} required value={editor.firstName} />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Middle name</label>
                    <input className="field-control px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, middleName: event.target.value }))} value={editor.middleName} />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Last name</label>
                    <input className="field-control px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, lastName: event.target.value }))} value={editor.lastName} />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Role</label>
                    <select className="field-control px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, role: event.target.value as EditorState["role"] }))} value={editor.role}>
                      <option value="Customer">Customer</option>
                      <option value="SALES">Sales</option>
                      <option value="ADMIN">Admin</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Email</label>
                    <input className="field-control px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, email: event.target.value }))} type="email" value={editor.email} />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Phone</label>
                    <input className="field-control px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, phone: event.target.value }))} value={editor.phone} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">
                      {editor.id ? "New password (optional)" : "Password"}
                    </label>
                    <input
                      className="field-control px-4 py-3 text-sm"
                      onChange={(event) => setEditor((current) => ({ ...current, password: event.target.value }))}
                      placeholder={editor.id ? "Leave blank to keep the current password" : "At least 8 characters"}
                      type="password"
                      value={editor.password}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Address</label>
                    <textarea className="field-control min-h-[100px] px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, address: event.target.value }))} value={editor.address} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Profile picture URL</label>
                    <input className="field-control px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, profilePicture: event.target.value }))} value={editor.profilePicture} />
                  </div>
                </div>

                <label className="field-soft flex items-center gap-3 px-4 py-3 text-sm">
                  <input checked={editor.emailConfirmed} onChange={(event) => setEditor((current) => ({ ...current, emailConfirmed: event.target.checked }))} type="checkbox" />
                  Email is confirmed
                </label>

                <div className="flex items-center gap-3">
                  <button className="brand-button rounded-full px-5 py-3 text-sm font-semibold text-white disabled:opacity-60" disabled={saving} type="submit">
                    {saving ? "Saving..." : editor.id ? "Save changes" : "Create user"}
                  </button>
                  <button className="rounded-full border border-black/10 px-5 py-3 text-sm font-semibold dark:border-white/10" onClick={() => setEditorOpen(false)} type="button">
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      {userToDelete ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm animate-fade-in">
          <div className="admin-card w-full max-w-md overflow-hidden rounded-[32px] bg-white p-6 shadow-2xl dark:bg-nv-field-bg animate-scale-in">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600 dark:bg-red-950/50 dark:text-red-400">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-semibold tracking-tight text-nv-ink">Delete user account?</h3>
                <p className="text-sm text-nv-slate leading-relaxed">
                  Are you sure you want to permanently delete{" "}
                  <span className="font-semibold text-nv-ink">
                    {[userToDelete.firstName, userToDelete.lastName].filter(Boolean).join(" ") || userToDelete.email || "this user"}
                  </span>
                  ? This action cannot be undone.
                </p>
                <div className="mt-3 rounded-2xl bg-red-50/50 p-3.5 text-xs text-red-800 dark:bg-red-950/20 dark:text-red-300 border border-red-100 dark:border-red-900/30">
                  <span className="font-semibold block mb-1">Cascade warning:</span>
                  All active sessions and cart items will be immediately destroyed. References in sales orders, coupons, articles, and logs will be set to NULL to preserve history.
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3 border-t border-black/5 pt-4 dark:border-white/10">
              <button
                className="rounded-full border border-black/10 px-5 py-2.5 text-sm font-semibold hover:bg-black/5 disabled:opacity-40 dark:border-white/10 dark:hover:bg-white/5"
                disabled={deleting}
                onClick={() => setUserToDelete(null)}
                type="button"
              >
                Cancel
              </button>
              <button
                className="rounded-full bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-40 shadow-sm"
                disabled={deleting}
                onClick={() => void handleDeleteConfirm()}
                type="button"
              >
                {deleting ? "Deleting..." : "Permanently Delete"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
